import { useState, useEffect, useCallback } from 'react';
import { DogSchool } from '@/data/dogSchools';
import { fetchPaginatedDogSchoolsByState } from '@/services/paginated/paginatedService';

interface UseStateSchoolsProps {
  countryId: string;
  stateId: string;
  pageSize?: number;
}

export const useStateSchools = ({ countryId, stateId, pageSize = 30 }: UseStateSchoolsProps) => {
  const [schools, setSchools] = useState<DogSchool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [cacheKey, setCacheKey] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  // Create a stable cache key for the current query
  useEffect(() => {
    if (countryId && stateId) {
      setCacheKey(`${countryId}-${stateId}-${pageSize}`);
    }
  }, [countryId, stateId, pageSize]);

  // Fetch schools on initial load with improved error handling
  useEffect(() => {
    const fetchSchools = async () => {
      if (countryId && stateId) {
        setIsLoading(true);
        setError(null);
        setPage(0);
        
        try {
          console.log(`Fetching schools for ${countryId}/${stateId} with pageSize ${pageSize}`);
          const result = await fetchPaginatedDogSchoolsByState(countryId, stateId, 0, pageSize);
          
          if (result.schools.length === 0) {
            console.log(`No schools found for ${countryId}/${stateId}, might be a state name matching issue`);
            // Try with variations of the state name
            const normalizedState = stateId.replace(/-/g, ' ');
            if (normalizedState !== stateId) {
              console.log(`Trying alternative state name: ${normalizedState}`);
              const alternativeResult = await fetchPaginatedDogSchoolsByState(countryId, normalizedState, 0, pageSize);
              if (alternativeResult.schools.length > 0) {
                console.log(`Found ${alternativeResult.schools.length} schools using alternative state name`);
                setSchools(alternativeResult.schools);
                setHasMore(alternativeResult.hasMore);
                setTotalCount(alternativeResult.totalCount);
                setIsLoading(false);
                return;
              }
            }
            
            // If still no results, try with the original name but different case
            const uppercaseState = stateId.toUpperCase();
            if (uppercaseState !== stateId) {
              console.log(`Trying uppercase state name: ${uppercaseState}`);
              const uppercaseResult = await fetchPaginatedDogSchoolsByState(countryId, uppercaseState, 0, pageSize);
              if (uppercaseResult.schools.length > 0) {
                console.log(`Found ${uppercaseResult.schools.length} schools using uppercase state name`);
                setSchools(uppercaseResult.schools);
                setHasMore(uppercaseResult.hasMore);
                setTotalCount(uppercaseResult.totalCount);
                setIsLoading(false);
                return;
              }
            }
            
            // Special case for Austrian states
            if (countryId === 'at' || countryId === 'austria') {
              // Try common spelling variations for Austrian states
              const stateVariations: Record<string, string[]> = {
                'steiermark': ['styria'],
                'niederoesterreich': ['niederösterreich', 'lower austria'],
                'oberoesterreich': ['oberösterreich', 'upper austria'],
                'kaernten': ['kärnten', 'carinthia'],
                'wien': ['vienna']
              };
              
              for (const [key, variations] of Object.entries(stateVariations)) {
                if (stateId.toLowerCase().includes(key) || variations.some(v => stateId.toLowerCase().includes(v))) {
                  for (const variant of [key, ...variations]) {
                    console.log(`Trying Austrian state variation: ${variant}`);
                    const variantResult = await fetchPaginatedDogSchoolsByState(countryId, variant, 0, pageSize);
                    if (variantResult.schools.length > 0) {
                      console.log(`Found ${variantResult.schools.length} schools using state variant: ${variant}`);
                      setSchools(variantResult.schools);
                      setHasMore(variantResult.hasMore);
                      setTotalCount(variantResult.totalCount);
                      setIsLoading(false);
                      return;
                    }
                  }
                }
              }
            }
          }
          
          setSchools(result.schools);
          setHasMore(result.hasMore);
          setTotalCount(result.totalCount);
          console.log(`Found ${result.schools.length} schools for ${countryId}/${stateId}`);
          
        } catch (error) {
          console.error("Error in useStateSchools:", error);
          setError("Failed to load dog schools. Please try again later.");
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchSchools();
  }, [countryId, stateId, pageSize]);
  
  // Load more schools when requested with improved caching
  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    
    // Speichere die aktuelle Scroll-Position
    const scrollPosition = window.scrollY;
    const nextPage = page + 1;
    
    try {
      setIsLoading(true);
      console.log(`Loading more schools for ${countryId}/${stateId}, page ${nextPage}`);
      
      const result = await fetchPaginatedDogSchoolsByState(countryId, stateId, nextPage, pageSize);
      
      // Check for duplicates to avoid adding the same school twice
      const existingIds = new Set(schools.map(school => school.id));
      const uniqueNewSchools = result.schools.filter(school => !existingIds.has(school.id));
      
      if (uniqueNewSchools.length > 0) {
        setSchools(prevSchools => [...prevSchools, ...uniqueNewSchools]);
        console.log(`Loaded ${uniqueNewSchools.length} more unique schools for ${countryId}/${stateId}`);
        
        // Stelle die Scroll-Position wieder her
        window.scrollTo(0, scrollPosition);
      } else {
        console.log(`No new unique schools loaded for ${countryId}/${stateId}`);
        // If no new schools, we've reached the end
        setHasMore(false);
      }
      
      setHasMore(result.hasMore && uniqueNewSchools.length > 0);
      setPage(nextPage);
      
    } catch (error) {
      console.error("Error loading more schools:", error);
      setError("Failed to load more dog schools. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [countryId, stateId, page, isLoading, hasMore, pageSize, schools]);
  
  return {
    schools,
    isLoading,
    error,
    hasMore,
    loadMore,
    totalCount
  };
};
